import { v4 as uuid } from "uuid";
import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { isApiError } from "@/services/Api";
import { summaryNoteService } from "@/services";

import { useBrandStore, useCaseStore, useMessageStore } from "@/stores";

import type { SummaryNote } from "@/models/SummaryNote";

export const useSummaryNotesStore = defineStore("summaryNotes", {
  scope: "case",
  state: (): State => ({ rawSummaryNotes: {} }),
  getters: {},
  actions: {
    async load(fileReference: string) {
      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      const fileNotes = this.rawSummaryNotes[fileReference] ?? { status: ModuleStatus.Initial, items: [] };

      if (fileNotes.status === ModuleStatus.Initial)
        this.summaryNotesLoaded(fileReference, { status: ModuleStatus.Loading, items: [] });
      else this.summaryNotesLoaded(fileReference, { status: ModuleStatus.Refreshing, items: fileNotes.items });

      try {
        const summaryNotes = await summaryNoteService.load(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          fileReference,
        );

        this.summaryNotesLoaded(fileReference, {
          status: ModuleStatus.Ready,
          items: summaryNotes.sort((l, r) => l.position - r.position).map((l, idx) => ({ ...l, position: idx + 1 })),
        });
      } catch (error) {
        console.error(error);
        this.summaryNotesLoaded(fileReference, { status: ModuleStatus.Error, items: [] });
      }
    },
    async addSummaryNote(fileReference: string, summaryText: string, evidenceTheme: string) {
      const reference = uuid();

      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      const maxPosition = Math.max(...this.rawSummaryNotes[fileReference].items.map((note) => note.position));

      const promise = summaryNoteService.save(
        brandStore.selectedClient,
        brandStore.selectedBrand,
        selectedCaseName,
        fileReference,
        {
          reference,
          summaryText,
          confidence: 0,
          evidenceTheme,
          position: maxPosition + 1,
        },
      );

      await this.commitSummaryNote(promise, fileReference);
    },
    async saveSummaryNote(fileReference: string, reference: string, summaryText: string) {
      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      const position = this.rawSummaryNotes[fileReference].items.find((note) => note.reference === reference)?.position;
      if (position == null) return;

      const promise = summaryNoteService.update(
        brandStore.selectedClient,
        brandStore.selectedBrand,
        selectedCaseName,
        fileReference,
        reference,
        { position, summaryText },
      );

      await this.commitSummaryNote(promise, fileReference);
    },
    async commitSummaryNote(promise: Promise<void>, fileReference: string) {
      const reference = uuid();

      try {
        await promise;
        this.load(fileReference);
      } catch (error) {
        if (isApiError<Record<"advisories", string[]>>(error) && error.status === 207) {
          useMessageStore().updateMessage({
            type: "warning",
            autoHide: true,
            title: "Summary Note",
            message: error.data?.advisories.join(", ") ?? "",
            showReload: false,
          });

          return { type: "SUCCESS", reference };
        }
      }
    },
    async deleteSummaryNote(fileReference: string, reference: string) {
      try {
        const brandStore = useBrandStore();
        const selectedCaseName = useCaseStore().selectedCaseName;
        if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

        await summaryNoteService.deleteSummaryNote(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          fileReference,
          reference,
        ); // Delete Data point

        this.load(fileReference); // Refresh list
      } catch (error) {
        console.error(error);
      }
    },
    async swapSummaryNotePosition<T extends Pick<SummaryNote, "summaryText" | "position" | "reference">>(
      fileReference: string,
      left: T,
      right: T,
    ) {
      try {
        const brandStore = useBrandStore();
        const selectedCaseName = useCaseStore().selectedCaseName;
        if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

        await Promise.all([
          summaryNoteService.update(
            brandStore.selectedClient,
            brandStore.selectedBrand,
            selectedCaseName,
            fileReference,
            left.reference,
            { position: right.position, summaryText: left.summaryText },
          ),
          summaryNoteService.update(
            brandStore.selectedClient,
            brandStore.selectedBrand,
            selectedCaseName,
            fileReference,
            right.reference,
            { position: left.position, summaryText: right.summaryText },
          ),
        ]);

        // Refetch all data points
        this.load(fileReference);
      } catch (error) {
        console.error(error);
      }
    },
    summaryNotesLoaded(fileReference: string, state: { status: ModuleStatus; items: SummaryNote[] }) {
      this.rawSummaryNotes[fileReference] = state;
    },
    storeSummaryNote(fileReference: string, summaryNote: SummaryNote) {
      const fileNotes = this.rawSummaryNotes[fileReference];
      const index = fileNotes.items.findIndex((item) => item.reference === summaryNote.reference);

      if (index < 0)
        this.summaryNotesLoaded(fileReference, { status: fileNotes.status, items: [...fileNotes.items, summaryNote] });
      else {
        this.summaryNotesLoaded(fileReference, {
          status: fileNotes.status,
          items: fileNotes.items.map((_, i) => (i === index ? summaryNote : _)),
        });
      }
    },
  },
});

interface State {
  rawSummaryNotes: Record<string, { status: ModuleStatus; items: SummaryNote[] }>;
}
